import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { downloadFormDocument, downloadReport, useReport } from '../../api/documents';
import { useEnumLabel } from '../../api/enums';
import { FORM_TYPES, type FormType } from '../../api/forms';
import type { Inspection } from '../../api/inspections';
import { ButtonLink, SummaryList, Table } from '../../components/idsk';
import { formatDateTime } from '../../lib/format';
import { DocumentLanguageSelect, DownloadButtons, defaultDocumentLanguage } from './documents/DocumentDownloads';

/** Form documents and the final report (PDF/DOCX) of one inspection. */
export function InspectionDocumentsTab({ inspection }: { inspection: Inspection }) {
  const { t, i18n } = useTranslation();
  const label = useEnumLabel();
  const [lang, setLang] = useState(defaultDocumentLanguage);
  const report = useReport(inspection.id, inspection.reportAvailable);
  const ui = i18n.language === 'en' ? 'en' : 'sk';

  return (
    <>
      <h2 className="govuk-heading-m">{t('documents.heading')}</h2>
      <p className="govuk-body">{t('documents.intro')}</p>
      <DocumentLanguageSelect id="documents-lang" value={lang} onChange={setLang} />

      <h3 className="govuk-heading-s">{t('documents.formsHeading')}</h3>
      <Table<FormType>
        caption={t('documents.formsCaption')}
        captionClassName="govuk-visually-hidden"
        rows={FORM_TYPES}
        rowKey={(f) => f}
        columns={[
          { key: 'form', header: t('documents.form'), rowHeader: true, render: (f) => label('formTypes', f) },
          {
            key: 'download',
            header: t('documents.download'),
            render: (f) => (
              <DownloadButtons id={`documents-${f}`} name={label('formTypes', f)} lang={lang} onDownload={(format, l) => downloadFormDocument(inspection.id, f, format, l)} />
            ),
          },
        ]}
      />

      <h3 className="govuk-heading-s">{t('report.title')}</h3>
      {inspection.reportAvailable ? (
        report.data ? (
          <>
            <SummaryList
              rows={[
                { key: t('report.generatedAt'), value: formatDateTime(report.data.generatedAt, ui) },
                { key: t('report.generatedBy'), value: report.data.generatedBy },
                { key: t('report.sha256'), value: <code className="app-hash">{report.data.sha256}</code> },
              ]}
            />
            <p className="govuk-body">{t('report.integrityHint')}</p>
            <div className="govuk-button-group">
              <ButtonLink to={`/inspekcie/${inspection.id}/sprava?jazyk=${lang}`} className="mr-2">{t('report.view.open')}</ButtonLink>
              <ButtonLink to={`/inspekcie/${inspection.id}/grafy?jazyk=${lang}`} variant="secondary">
                {t('charts.open')}
              </ButtonLink>
            </div>
            <DownloadButtons id="documents-report" name={t('report.title')} lang={lang} onDownload={(format, l) => downloadReport(inspection.id, format, l)} />
          </>
        ) : (
          <p className="govuk-body">{t('common.loading')}</p>
        )
      ) : (
        <p className="govuk-body">{t('report.notYet')}</p>
      )}
    </>
  );
}
